import axios from 'axios';

const axiosServices = axios.create({ baseURL: '' });

// Import AUTH_STORAGE_KEY from JWTContext
const AUTH_STORAGE_KEY = 'mantis-react-ts-auth';

// always send/receive cookies (PHPSESSID)
axiosServices.defaults.withCredentials = true;

// Smart auto-refresh system
const DEPLOYMENT_STORAGE_KEY = 'deployment_timestamp';
const REFRESH_STORAGE_KEY = 'last_refresh_timestamp';
const REFRESH_FLAG_KEY = 'need_to_refresh';
const REFRESH_INTERVAL = 3 * 60 * 60 * 1000; // 3 hours — memory hygiene (deploy detection is separate)
let refreshPending = false;
let refreshScheduled = false;

/**
 * Unsafe = operator mid add/edit/delete in a modal.
 * Open MUI Dialog only (not the permanent nav Drawer).
 * No reload while unsafe; flag is processed on next safe interaction.
 */
const isUnsafeState = () => {
  const dialogs = document.querySelectorAll('.MuiDialog-root');
  for (const dialog of dialogs) {
    if (dialog.getAttribute('aria-hidden') === 'true') continue;
    if (dialog.classList.contains('MuiModal-hidden')) continue;
    const style = window.getComputedStyle(dialog);
    if (style.display === 'none' || style.visibility === 'hidden') continue;
    return true;
  }
  return false;
};

// ==============================|| FLAG-BASED REFRESH MECHANISM ||============================== //

/**
 * Sets the 'need to refresh' flag
 * @param {string} reason - Reason for setting the flag
 */
const setRefreshFlag = (reason = 'unknown') => {
  localStorage.setItem(REFRESH_FLAG_KEY, 'true');
  localStorage.setItem('refresh_reason', reason);
};

/**
 * Clears the 'need to refresh' flag
 */
const clearRefreshFlag = () => {
  localStorage.removeItem(REFRESH_FLAG_KEY);
  localStorage.removeItem('refresh_reason');
};

/**
 * Checks if refresh flag is set
 * @returns {boolean} True if refresh is needed
 */
const isRefreshFlagSet = () => {
  return localStorage.getItem(REFRESH_FLAG_KEY) === 'true';
};

/**
 * Performs the actual refresh and stores the timestamp
 */
const performRefresh = () => {
  const reason = localStorage.getItem('refresh_reason') || 'unknown';

  // Store refresh timestamp
  const refreshTimestamp = Date.now();
  localStorage.setItem(REFRESH_STORAGE_KEY, refreshTimestamp.toString());

  // Clear refresh flag
  clearRefreshFlag();

  // Reset refresh states
  refreshPending = false;
  refreshScheduled = false;

  // Perform the refresh
  window.location.reload();
};

/**
 * Checks if 3-hour hygiene refresh is due.
 * Sets flag and reloads only when safe (no open modal).
 */
const checkPeriodicRefresh = () => {
  const lastRefresh = localStorage.getItem(REFRESH_STORAGE_KEY);

  if (!lastRefresh) {
    localStorage.setItem(REFRESH_STORAGE_KEY, Date.now().toString());
    return;
  }

  const lastRefreshTime = parseInt(lastRefresh, 10);
  const timeSinceLastRefresh = Date.now() - lastRefreshTime;

  if (timeSinceLastRefresh >= REFRESH_INTERVAL) {
    console.log('3-hour refresh due; reload when safe (no open modal)...');
    setRefreshFlag('3-hour timer');
    handleRefreshCheck();
  }
};

/**
 * Main refresh mechanism - checks flag and decides whether to refresh immediately or schedule it
 */
const handleRefreshCheck = () => {
  if (!isRefreshFlagSet()) {
    return; // No refresh needed
  }

  const reason = localStorage.getItem('refresh_reason') || 'unknown';

  if (isUnsafeState()) {
    scheduleRefresh();
  } else {
    performRefresh();
  }
};

/**
 * Schedules a refresh to be performed when safe
 */
const scheduleRefresh = () => {
  if (refreshScheduled) {
    return; // Already scheduled
  }

  refreshScheduled = true;
  refreshPending = true;
  setupSmartRefresh();
};

/**
 * Smart refresh - refresh when user interacts and it's safe
 */
const setupSmartRefresh = () => {
  const handleUserInteraction = () => {
    if (refreshPending && !isUnsafeState()) {
      performRefresh();
    }
  };

  // Listen for various user interactions
  document.addEventListener('click', handleUserInteraction, { once: true });
  document.addEventListener('keydown', handleUserInteraction, { once: true });
  document.addEventListener('scroll', handleUserInteraction, { once: true });
};

/**
 * Sets up user activity monitoring for refresh checks
 */
const setupUserActivityMonitoring = () => {
  const handleUserActivity = () => {
    // Check for scheduled refresh
    if (refreshScheduled && refreshPending) {
      if (!isUnsafeState()) {
        performRefresh();
      }
    }

    // Check for periodic refresh (3-hour timer)
    checkPeriodicRefresh();

    // Check if refresh flag is set (deploy or timer)
    handleRefreshCheck();
  };

  // Listen for user interactions
  document.addEventListener('click', handleUserActivity);
  document.addEventListener('keydown', handleUserActivity);
  document.addEventListener('scroll', handleUserActivity);
  document.addEventListener('mousemove', handleUserActivity);

  // Also check on page visibility change (user returns to tab)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      handleUserActivity();
    }
  });
};

// Initialize user activity monitoring
setupUserActivityMonitoring();

// Reset 3-hour hygiene countdown after a full page load/reload
const resetOnPageLoad = () => {
  localStorage.setItem(REFRESH_STORAGE_KEY, Date.now().toString());
};

resetOnPageLoad();

/**
 * Deployment update check (axios fallback).
 * Primary check is in index.jsx before React (boot — no modal yet).
 * Mid-session: set flag and reload only when safe (no open dialog).
 */
const checkDeploymentUpdate = (response) => {
  const deployTimestamp = response.headers['x-deploy-timestamp'];

  if (!deployTimestamp) {
    return;
  }

  const storedTimestamp = localStorage.getItem(DEPLOYMENT_STORAGE_KEY);

  if (storedTimestamp && storedTimestamp !== deployTimestamp) {
    console.log('Deployment detected in API response; reload when safe (no open modal)...');
    localStorage.setItem(DEPLOYMENT_STORAGE_KEY, deployTimestamp);
    setRefreshFlag('deployment update');
    handleRefreshCheck();
    return;
  }

  localStorage.setItem(DEPLOYMENT_STORAGE_KEY, deployTimestamp);
};

// ==============================|| AXIOS - FOR APP SERVICES ||============================== //

axiosServices.interceptors.request.use(
  async (config) => {
    // Send client deployment timestamp to server
    const clientTimestamp = localStorage.getItem(DEPLOYMENT_STORAGE_KEY);
    if (clientTimestamp) {
      config.headers['X-Deploy-Timestamp'] = clientTimestamp;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosServices.interceptors.response.use(
  (response) => {
    // Check for deployment updates
    checkDeploymentUpdate(response);

    // Check for reload_permissions signal
    if (response?.data?.reload_permissions === true && response?.data?.permissions) {
      // Clear old permissions and set new ones
      const newPermissions = response.data.permissions;

      // Update sessionStorage
      const authStorageKey = 'mantis-react-ts-auth';
      const storedAuth = sessionStorage.getItem(authStorageKey);
      if (storedAuth) {
        try {
          const authData = JSON.parse(storedAuth);
          if (authData.user) {
            // Clear old permissions and set new ones
            authData.user.permissions = newPermissions;
            sessionStorage.setItem(authStorageKey, JSON.stringify(authData));

            // Dispatch event to update context
            window.dispatchEvent(new CustomEvent('reloadPermissions', {
              detail: { permissions: newPermissions }
            }));
          }
        } catch (error) {
          console.error('Error updating permissions in sessionStorage:', error);
        }
      }
    }

    return response;
  },
  (error) => {

    // Check for redirect to login flag (highest priority)
    const shouldRedirectToLogin = error?.response?.data?.redirect_to_login === true;
    // Check for authentication failures
    const isAuthError =
      error?.response?.status === 401 ||
      (error?.response?.data?.success === false &&
        (error?.response?.data?.message?.toLowerCase().includes('authentication') ||
          error?.response?.data?.message?.toLowerCase().includes('not authenticated') ||
          error?.response?.data?.message?.toLowerCase().includes('login') ||
          error?.response?.data?.message?.toLowerCase().includes('logged out')));

    if (shouldRedirectToLogin || isAuthError) {
      // Only store logout message if this is NOT a manual logout
      const isManualLogout = sessionStorage.getItem('manualLogout');

      // Don't redirect if we just logged in (check for recent login)
      const lastLoginTime = sessionStorage.getItem('lastLoginTime');
      const now = Date.now();
      const timeSinceLogin = now - (lastLoginTime ? parseInt(lastLoginTime) : 0);

      if (!isManualLogout && timeSinceLogin > 5000) { // Only redirect if more than 5 seconds since login
        // Only show custom message for specific system logout reasons
        const serverMessage = error?.response?.data?.message || '';
        if (serverMessage.includes('signed in from another device') ||
          serverMessage.includes('logged out because') ||
          serverMessage.includes('session expired')) {
          // This is a system logout, show the server message
          sessionStorage.setItem('logoutMessage', serverMessage);
        }
        // For other cases like "No user session found", don't show any message
      } else {
        // Clear the manual logout flag
        sessionStorage.removeItem('manualLogout');
      }

      // Clear ALL authentication state immediately
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
      sessionStorage.removeItem('sessionPrefs');
      sessionStorage.removeItem('mantis-react-ts-config');

      // Clear any stored user preferences
      sessionStorage.removeItem('prefs');

      // Trigger force logout event for context cleanup
      window.dispatchEvent(new CustomEvent('forceLogout'));

      // Only redirect if we're NOT already on the login page
      if (window.location.pathname != '/auth/login') {
        // Force a complete page reload to ensure all state is cleared
        try {
          window.location.replace('/auth/login');
        } catch (e) {
          window.location.href = '/auth/login';
        }
      }

      // Prevent further error handling
      return Promise.resolve();
    }
    return Promise.reject((error.response && error.response.data) || 'Request failed');
  }
);

export default axiosServices;

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await axiosServices.get(url, { ...config });
  return res.data;
};

export const fetcherPost = async (args) => {
  const [url, data, config] = Array.isArray(args) ? args : [args, {}];
  const res = await axiosServices.post(url, data, { ...config });
  return res.data;
};